import { strings } from '../strings';

/** Timestamps are shown in the browser's local time. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '–';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return '–';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/** Hours and minutes in local time, for compact labels such as "Plan · 10:42". */
export function formatClock(value: string | null | undefined): string {
  if (!value) return '–';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * A relative time for lists ("just now", "10 min ago", "3 h ago", "yesterday", "3 days ago"), and the date
 * ("12 Sep", with the year when it isn't this year's) for anything older than a week. The absolute time
 * goes into the title (`formatDateTime`).
 */
export function formatRelative(value: string | null | undefined, now = Date.now()): string {
  if (!value) return '–';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const s = strings.relative;
  const seconds = Math.max(0, Math.round((now - date.getTime()) / 1000));
  if (seconds < 45) return s.justNow;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return s.minutes(minutes);
  const hours = Math.round(minutes / 60);
  if (hours < 24) return s.hours(hours);
  const days = Math.floor(hours / 24);
  if (days === 1) return s.yesterday;
  if (days < 7) return s.days(days);
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

export function durationMs(start: string | null, end: string | null, now = Date.now()): number | null {
  if (!start) return null;
  const from = new Date(start).getTime();
  const to = end ? new Date(end).getTime() : now;
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.max(0, to - from);
}

export function formatDuration(ms: number | null): string {
  if (ms === null) return '–';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function formatJsonValue(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/** A day such as "9 Jan 2027" (recoverable-until dates). */
export function formatDay(value: string | null | undefined): string {
  if (!value) return '–';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "31 October 2026" for a calendar day (`yyyy-MM-dd`, a release date), without a time zone shift. */
export function formatReleaseDate(date: string): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Bytes for size counters: "20 B", "25 KB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  return `${Number.isInteger(kb) ? kb : kb.toFixed(1)} KB`;
}
